import * as React from 'react';
import { Suspense, useEffect, useState } from 'react';

import './TutorialStatusSection.scss';
import { MemoizedTutorialProgressBar } from './TutorialProgressBar';
import { useSelector } from 'react-redux';
import {
  getCurrentlyViewedStep,
  getMaxUnlockedTutorialStep,
  getTutorialStartDate,
  getTutorialStatus,
  getTutorialTotalSteps,
} from '../../reducers/TutorialReducer/TutorialSelectors';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { challengeScoreCompetitionSelector } from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import { Loader, Popup } from 'semantic-ui-react';
import moment from 'moment';
import { chapters, stepToChapter } from './chapterStructure';
import { TutorialCongratulationsScreen } from './TutorialCongratulationsScreen';
import { useMeasure } from 'react-use';
import { useDebounce } from 'use-debounce';

export function TutorialStatusSection() {
  const [ref, { width }] = useMeasure();
  const [debouncedWidth] = useDebounce(width, 200);
  const maxUnlockedPage = useSelector(getMaxUnlockedTutorialStep);
  const pageCount = useSelector(getTutorialTotalSteps);
  const startDate = useSelector(getTutorialStartDate);
  const tutorialStatus = useSelector(getTutorialStatus);
  const currentlyViewedStep = useSelector(getCurrentlyViewedStep);

  const [closedCongratsChapter, setClosedCongratsChapter] = useState(0);

  const handleCongratsClose = () => {
    if (currentChapter?.number) {
      setClosedCongratsChapter(currentChapter?.number);
    }
  };

  const effectivePage = tutorialStatus === 'COMPLETED' ? pageCount : maxUnlockedPage ?? 0;

  const progressGraphColor = 'rgb(0, 174, 0)';

  const pointsGraphColor = 'rgb(0, 116, 196)';
  const challengeScore = useSelector(challengeScoreCompetitionSelector) ?? 0;
  const maxChallengeScore = 10000;
  const scoreProgressLabel = `${challengeScore} / ${maxChallengeScore}`;

  const daysElapsed = moment().diff(moment(startDate), 'days');
  const tutorialDaysLimit = 30;
  const daysLeft = tutorialDaysLimit - daysElapsed;

  const minAlphaScore = 1000;
  const maxAlphaScore = 2000;
  const remainingScore = maxChallengeScore - challengeScore;
  const minAlphasToSubmit = Math.ceil(remainingScore / maxAlphaScore);
  const maxAlphasToSubmit = Math.ceil(remainingScore / minAlphaScore);
  const daysToSubmit = Math.ceil(remainingScore / 2000);

  const currentChapter = stepToChapter(currentlyViewedStep);

  const stepsSinceChapterStart = effectivePage - (currentChapter?.firstStepId ?? 0);

  const isChapterComplete = stepsSinceChapterStart === 0 && maxUnlockedPage === currentlyViewedStep;
  const isCongratsShown = isChapterComplete && closedCongratsChapter !== currentChapter?.number && currentChapter?.firstStepId !== 0;
  useEffect(() => {
    if (isCongratsShown) {
      const background = document.querySelector('.tutorial-background');
      if (background) {
        background.scrollTo(0, 0);
      }
    }
  }, [isCongratsShown]);

  if (!currentChapter) {
    return null;
  }

  const chapterStepCount = currentChapter.lastStepId - currentChapter.firstStepId + 1;
  const progressLabelStep = stepsSinceChapterStart > chapterStepCount ? chapterStepCount : stepsSinceChapterStart;
  const tutorialProgressLabel = `${progressLabelStep} / ${chapterStepCount}`;

  return (
    <>
      {/*@ts-ignore*/}
      <div className="tutorial-status" ref={ref}>
        <div className="tutorial-status__header">
          <h3>
            Chapter {currentChapter.number} of {chapters.length}: {currentChapter.name}
          </h3>
        </div>
        <div className="tutorial-status__content">
          <Suspense fallback={<Loader size={'big'} />}>
            <Popup
              position={'left center'}
              content={
                <div>
                  <div>
                    <FormattedMessageHelper id={'navigatorTutorial.daysLeftHint'} values={{ daysLeft }} />
                  </div>
                  <div>
                    <FormattedMessageHelper id={'navigatorTutorial.daysHint'} values={{ daysElapsed }} />
                  </div>
                </div>
              }
              trigger={<div className="tutorial-status__days">D-{daysLeft}</div>}
            />

            <div className="tutorial-status__progress">
              <div className="tutorials-status__bar-container">
                <div className="tutorials-status__bar-label">
                  <FormattedMessageHelper id={'navigatorTutorial.learningProgress'} />
                </div>
                <MemoizedTutorialProgressBar
                  value={stepsSinceChapterStart}
                  maxValue={chapterStepCount}
                  progressLabel={tutorialProgressLabel}
                  graphColor={progressGraphColor}
                  width={width}
                />
              </div>

              <Popup
                position={'bottom left'}
                style={{ marginTop: '-8px' }}
                content={
                  <div>
                    <div>
                      <FormattedMessageHelper id={'navigatorTutorial.challengeHint'} values={{ daysLeft }} />
                    </div>
                    {challengeScore < maxChallengeScore && (
                      <>
                        <br />
                        <div>
                          <FormattedMessageHelper
                            id={'navigatorTutorial.scoreHint'}
                            values={{ minAlphasToSubmit, maxAlphasToSubmit, daysToSubmit }}
                          />
                        </div>
                        <div>
                          <FormattedMessageHelper id={'navigatorTutorial.daysToSubmitHint'} values={{ daysToSubmit }} />
                        </div>
                      </>
                    )}
                  </div>
                }
                trigger={
                  <a href="/competition/challenge" target="_blank" className={'tutorial-status__challenge-link'}>
                    <div className="tutorials-status__bar-container">
                      <div className="tutorials-status__bar-label">
                        <FormattedMessageHelper id={'navigatorTutorial.reachingGold'} />
                      </div>
                      <MemoizedTutorialProgressBar
                        value={challengeScore}
                        maxValue={maxChallengeScore}
                        progressLabel={scoreProgressLabel}
                        graphColor={pointsGraphColor}
                        width={debouncedWidth}
                      />
                    </div>
                  </a>
                }
              />
            </div>
          </Suspense>
        </div>
      </div>
      {isCongratsShown && (
        <div className="tutorial-status">
          <TutorialCongratulationsScreen
            chapterName={currentChapter.name}
            congratulationsText={currentChapter.completionMessage}
            onClose={handleCongratsClose}
          />
        </div>
      )}
    </>
  );
}

export const MemoizedTutorialStatusSection = React.memo(TutorialStatusSection);
